import { useState } from "react";
import { rankingCompanies } from "@/mocks/rankingData";

const companyUrls: Record<number, string> = {
  1: "https://www.ine-grp.co.jp/media/broadband/refresh/ribon_dr5/",
  2: "https://www.ine-grp.co.jp/media/broadband/refresh/oct_dr5/",
  3: "https://www.ine-grp.co.jp/media/broadband/refresh/looop_dr5/",
  4: "https://www.ine-grp.co.jp/media/broadband/refresh/shiro_dr5/",
  5: "https://www.ine-grp.co.jp/media/broadband/refresh/au_dr5/",
};

export default function DetailSection() {
  const [activeRank, setActiveRank] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayRank, setDisplayRank] = useState(1);
  const company = rankingCompanies.find((c) => c.rank === displayRank)!;

  const handleTabClick = (rank: number) => {
    if (rank === activeRank) return;
    setIsTransitioning(true);
    setActiveRank(rank);
    setTimeout(() => {
      setDisplayRank(rank);
      setIsTransitioning(false);
    }, 300);
  };

  const statsItems = [
    { label: "基本料金", value: company.basicFee },
    { label: "燃料費調整額", value: company.fuelAdjust },
    { label: "初期費用", value: company.initialCost },
    { label: "解約金", value: company.cancelFee },
    { label: "切り替え期間", value: company.switchingDays },
    { label: "引越し申込期限", value: company.movingDeadline },
    { label: "供給エリア", value: company.area },
  ];

  return (
    <section id="detail" className="py-20 bg-brand-50/50">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            各社詳細
          </h2>
          <p className="text-gray-500 text-sm">気になる会社をクリックして詳細を確認</p>
        </div>

        {/* Tab selector: 1〜5 all visible */}
        <div className="flex justify-center mb-10">
          <div className="flex items-center gap-3">
            {rankingCompanies.map((c) => {
              const isActive = c.rank === activeRank;
              return (
                <button
                  key={c.rank}
                  onClick={() => handleTabClick(c.rank)}
                  className={`flex items-center justify-center rounded-full font-bold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    isActive
                      ? "w-14 h-14 bg-orange-500 text-white text-2xl"
                      : "w-10 h-10 bg-gray-200 text-gray-500 text-base hover:bg-gray-300"
                  }`}
                >
                  {c.rank}
                </button>
              );
            })}
          </div>
        </div>

        {/* Detail card */}
        <div
          className={`bg-white rounded-2xl border border-gray-200 overflow-hidden transition-all duration-300 ease-out ${
            isTransitioning ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"
          }`}
        >
          <div className="flex flex-col md:flex-row">
            {/* Left: Image */}
            <div className="md:w-80 flex-shrink-0">
              <div className="h-60 md:h-full relative">
                <img
                  src={company.image}
                  alt={company.name}
                  className="w-full h-full object-contain object-center"
                />

              </div>
            </div>

            {/* Right: Info */}
            <div className="flex-1 p-7">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{company.name}</h3>
                  <p className="text-gray-500 text-sm">{company.tagline}</p>
                </div>
                <a
                  href={companyUrls[company.rank] ?? "#"}
                  target={companyUrls[company.rank] ? "_blank" : undefined}
                  rel={companyUrls[company.rank] ? "noopener noreferrer" : undefined}
                  className="whitespace-nowrap bg-brand-500 hover:bg-brand-600 text-white font-bold px-6 py-3 rounded-lg text-sm transition-all cursor-pointer flex items-center gap-2"
                >
                  公式サイトで申し込む
                  <i className="ri-external-link-line" />
                </a>
              </div>

              {/* Comment */}
              <div className="bg-brand-50 border-l-4 border-brand-400 rounded-r-lg px-4 py-3 mb-4">
                <p className="text-sm text-brand-900 leading-relaxed">{company.comment}</p>
              </div>

              {/* Key stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {statsItems.map((item, i) => (
                  <div key={i} className="bg-brand-50/50 rounded-lg p-3">
                    <div className="text-xs text-brand-600/70 mb-1">{item.label}</div>
                    <div className="text-sm font-bold text-gray-800 leading-snug whitespace-pre-line">{item.value}</div>
                  </div>
                ))}
              </div>

              {/* Recommended */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-user-heart-line text-brand-500" />
                  <span className="text-xs font-bold text-gray-700">おすすめの方</span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{company.recommended}</p>
              </div>

              {/* Campaign */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-gift-line text-amber-500" />
                  <span className="text-xs font-bold text-amber-800">現在のキャンペーン</span>
                </div>
                <p className="text-sm font-semibold text-amber-900">{company.campaign}</p>
              </div>

              {/* Points */}
              <div className="space-y-2">
                {company.points.map((pt, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-brand-100 flex-shrink-0">
                      <i className="ri-check-line text-brand-500 text-xs" />
                    </span>
                    {pt}
                  </div>
                ))}
              </div>

              {/* Switching support */}
              <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <i className="ri-swap-line text-green-600 text-sm" />
                  <span className="text-xs font-bold text-green-800">切り替え手続き</span>
                </div>
                <p className="text-sm text-green-900 mt-1">{company.switchingSupport}</p>
              </div>

              {/* Bottom CTA button */}
              <div className="mt-8 flex justify-center">
                <a
                  href={companyUrls[company.rank] ?? "#"}
                  target={companyUrls[company.rank] ? "_blank" : undefined}
                  rel={companyUrls[company.rank] ? "noopener noreferrer" : undefined}
                  className="whitespace-nowrap bg-brand-500 hover:bg-brand-600 text-white font-bold px-10 py-4 rounded-xl text-base transition-all cursor-pointer flex items-center gap-2 shadow-sm"
                >
                  公式サイトで申し込む
                  <i className="ri-external-link-line text-lg" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}